import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength } from "@/components/PasswordStrength";
import { Building2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const token = new URLSearchParams(window.location.search).get("token");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    if (password !== confirm) { setError("Le password non coincidono."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Impossibile reimpostare la password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 fade-in">
        <div className="h-11 w-11 rounded-md bg-[#007BFF] flex items-center justify-center mx-auto mb-6"><Building2 className="h-6 w-6 text-white" /></div>
        {done ? (
          <div data-testid="reset-success" className="text-center flex flex-col items-center gap-3">
            <CheckCircle2 className="h-12 w-12 text-[#007BFF]" />
            <h1 className="font-head text-2xl font-bold">Password aggiornata</h1>
            <p className="text-muted-foreground text-sm">Ora puoi accedere con la nuova password.</p>
            <Button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-2 h-11 bg-[#007BFF] hover:bg-[#0056B3] text-white w-full">Vai all'accesso</Button>
          </div>
        ) : (
          <>
            <h1 className="font-head text-2xl font-bold text-center">Reimposta la password</h1>
            <p className="text-muted-foreground text-sm text-center mt-2">Scegli una nuova password per il tuo account.</p>
            {!token && <p className="text-red-600 text-sm mt-4 text-center">Link non valido.</p>}
            {error && (
              <div data-testid="reset-error" className="flex items-start gap-2 mt-5 p-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{error}</span>
              </div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
              <PasswordInput data-testid="reset-password" required placeholder="Nuova password (min. 8 caratteri)" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
              <PasswordStrength password={password} />
              <PasswordInput data-testid="reset-confirm" required placeholder="Conferma password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" />
              <Button type="submit" data-testid="reset-submit" disabled={busy || !token} className="w-full h-11 bg-[#007BFF] hover:bg-[#0056B3] text-white text-base">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
